import { SERVICE_STATUS, USER_ROLES } from '@bagonboard/wiseman';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useRoleConditional } from '#components';
import CustomInput from '../../../components/CustomInput/CustomInput';
import TableDateRange from '../../../components/TableDateRange/TableDateRange';
import TableMultiSelect from '../../../components/TableDateRange/TableMultiSelect';
import * as areasActions from '../../../reducers/Areas/actions';
import * as actions from '../../../reducers/Services/actions';
import { historyPropTypes } from '../../../types';
import {
  Area, Code, Delivery, Details, Name, Pickup, Status, Incident,
} from './components';
import { useInputFilterStyles } from './styles';

const serviceStatus = Object.values(SERVICE_STATUS);

const InputFilter = ({
  onChange, labelText, id, maxLength, filter,
}) => {
  const classes = useInputFilterStyles();

  return (
    <div className={classes.root}>
      <CustomInput
        type="text"
        labelText={labelText}
        id={id}
        maxLength={maxLength}
        formControlProps={{ fullWidth: true }}
        inputProps={{
          type    : 'text',
          maxLength,
          onChange: event => onChange(event.target.value),
        }}
        value={filter && filter.value && filter.value}
      />
    </div>
  );
};

function Services({
  history,
  servicesFetch,
  services: {
    data: { services, total },
  },
  services: { filters },
  services: { loading },
  areasFetch,
  areas,
}) {
  const role = useRoleConditional();

  useEffect(() => {
    async function fetchServicesData() {
      await Promise.all([areasFetch({})]);
    }
    if (areas.data.length === 0) {
      fetchServicesData();
    }
  }, []);

  const isNotLFAgentOrOperator = role.notIncludes([
    USER_ROLES.HANDLING_COMPANY_AGENT,
    USER_ROLES.HANDLING_COMPANY_OPERATOR,
  ]);

  const columns = [
    {
      Header    : 'Area',
      accessor  : 'from.cityCode',
      Cell      : Area,
      minWidth  : 70,
      sortable  : false,
      filterable: isNotLFAgentOrOperator,
      Filter    : TableMultiSelect({
        id             : 'Area',
        labelText      : 'Area',
        dataTableLength: services.length,
        options        : areas.data.map(({ _id, code }) => ({
          value: _id,
          text : code,
        })),
      }),
    },
    {
      Header  : 'Status',
      accessor: 'status',
      Cell    : Status,
      minWidth: 150,
      sortable: false,
      Filter  : TableMultiSelect({
        id             : 'Status',
        labelText      : 'Status',
        dataTableLength: services.length,
        options        : serviceStatus.map(value => ({
          value,
          text: value,
        })),
      }),
    },
    {
      Header  : 'Code',
      accessor: 'code',
      Cell    : Code,
      minWidth: 100,
      sortable: false,
      Filter  : props => <InputFilter {...props} labelText="6 digits code" id="code" maxLength="6" />,
    },
    {
      Header  : 'Full Name',
      accessor: 'order.customer.name',
      Cell    : Name,
      minWidth: 151,
      sortable: false,
      Filter  : props => <InputFilter {...props} labelText="Name and surname" id="name" />,
    },
    {
      Header    : 'Pick-up',
      accessor  : 'pickupTime',
      Cell      : Pickup,
      minWidth  : 180,
      filterable: false,
      sortable  : false,
    },
    {
      Header    : 'Delivery',
      accessor  : 'deliveryTime',
      Cell      : Delivery,
      minWidth  : 180,
      filterable: false,
      sortable  : false,
    },
    {
      Header    : '',
      accessor  : 'incident',
      sortable  : false,
      filterable: false,
      minWidth  : 32,
      Cell      : columnProp => <Incident {...columnProp} />,
    },
    {
      Header    : '',
      accessor  : '_id',
      sortable  : false,
      filterable: false,
      minWidth  : 104,
      Cell      : columnProp => <Details history={history} {...columnProp} />,
    },
  ];
  return (
    <TableDateRange
      data={services}
      filters={filters}
      loading={loading}
      columns={columns}
      total={total}
      dataFetch={servicesFetch}
    />
  );
}

Services.propTypes = {
  history      : historyPropTypes.isRequired,
  servicesFetch: PropTypes.func.isRequired,
  areasFetch   : PropTypes.func.isRequired,
  areas        : PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        _id : PropTypes.string,
        text: PropTypes.string,
      })
    ),
    error: PropTypes.string,

    loading: PropTypes.bool,
  }),
  services: PropTypes.shape({
    data: PropTypes.shape({
      services: PropTypes.arrayOf(PropTypes.object),
      limit   : PropTypes.string,
      offset  : PropTypes.string,
      retrieve: PropTypes.number,
      total   : PropTypes.number,
    }),
    error  : PropTypes.string,
    filters: PropTypes.shape({
      from: PropTypes.number,
      to  : PropTypes.number,
    }),
    loading: PropTypes.bool,
  }),
};

Services.defaultProps = {
  services: {},
  areas   : {},
};

InputFilter.propTypes = {
  onChange : PropTypes.func.isRequired,
  labelText: PropTypes.string.isRequired,
  id       : PropTypes.string.isRequired,
  maxLength: PropTypes.string,
  filter   : PropTypes.shape({
    value: PropTypes.oneOf(PropTypes.string, PropTypes.number),
  }),
};

InputFilter.defaultProps = {
  maxLength: undefined,
  filter   : {},
};

const mapStateToProps = ({ services, areas }) => ({
  services,
  areas,
});

const mapDispatchToProps = dispatch => ({
  servicesFetch: props => dispatch(actions.servicesFetch(props)),
  areasFetch   : props => dispatch(areasActions.areasFetch(props)),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(Services);
